import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, ReferenceLine,
} from 'recharts';
import api from '../../../api/api';

const MONTH_KEYS = [
  'month_jan', 'month_feb', 'month_mar', 'month_apr',
  'month_may', 'month_jun', 'month_jul', 'month_aug',
  'month_sep', 'month_oct', 'month_nov', 'month_dec',
];

const YEAR_BAR_COLORS = [
  '#3b82f6', '#f97316', '#8b5cf6', '#ec4899', '#14b8a6',
  '#eab308', '#6366f1', '#f43f5e', '#06b6d4', '#84cc16',
];

const ProfitTab = ({ bookings }) => {
  const { t } = useTranslation();
  const [currencySymbol, setCurrencySymbol] = useState('');
  const [selectedYears, setSelectedYears] = useState(null);
  const [costsByYear, setCostsByYear] = useState({});
  const fetchedRef = useRef(new Set());

  useEffect(() => {
    api.get('/currencies/')
      .then(res => {
        const def = res.data.find(c => c.is_default);
        if (def) setCurrencySymbol(def.symbol);
      })
      .catch(() => {});
  }, []);

  const incomeByYearMonth = useMemo(() => bookings.reduce((acc, booking) => {
    const date = new Date(booking.start_date);
    const year = date.getFullYear();
    const month = date.getMonth();
    if (!isNaN(year)) {
      if (!acc[year]) acc[year] = Array(12).fill(0);
      acc[year][month] += parseFloat(booking.total_price) || 0;
    }
    return acc;
  }, {}), [bookings]);

  const availableYears = useMemo(
    () => Object.keys(incomeByYearMonth).map(Number).sort((a, b) => b - a),
    [incomeByYearMonth]
  );

  // Fetch costs for any year not yet fetched
  useEffect(() => {
    availableYears.forEach(year => {
      if (fetchedRef.current.has(year)) return;
      fetchedRef.current.add(year);
      api.get(`/costs/reports/costs-report/?year=${year}`)
        .then(res => {
          const monthTotals = Array(12).fill(0);
          (res.data.months || []).forEach(m => {
            monthTotals[m.month - 1] = parseFloat(m.total) || 0;
          });
          setCostsByYear(prev => ({ ...prev, [year]: monthTotals }));
        })
        .catch(() => {
          setCostsByYear(prev => ({ ...prev, [year]: Array(12).fill(0) }));
        });
    });
  }, [availableYears]);

  const effectiveSelectedYears = selectedYears === null ? availableYears : selectedYears;

  const toggleYear = (year) => {
    setSelectedYears(prev => {
      const base = prev === null ? availableYears : prev;
      return base.includes(year) ? base.filter(y => y !== year) : [...base, year];
    });
  };

  const chartData = useMemo(() => MONTH_KEYS.map((key, idx) => {
    const entry = { month: t(key) };
    effectiveSelectedYears.forEach(year => {
      const income = (incomeByYearMonth[year]?.[idx]) || 0;
      const costs = (costsByYear[year]?.[idx]) || 0;
      entry[String(year)] = parseFloat((income - costs).toFixed(2));
    });
    return entry;
  }), [effectiveSelectedYears, incomeByYearMonth, costsByYear, t]);

  const currencyFormatter = (value) => `${currencySymbol}${Number(value).toFixed(2)}`;

  return (
    <div className="w-full">
      <h2 className="text-2xl font-bold mb-6">{t('yearly_profit')}</h2>

      {availableYears.length === 0 ? (
        <p className="text-gray-500">—</p>
      ) : (
        <div className="bg-white p-6 rounded-lg shadow-md border border-gray-200">
          {/* Year checkboxes */}
          <div className="flex flex-wrap items-center gap-4 mb-6">
            <span className="text-sm font-medium text-gray-600">{t('select_years')}:</span>
            {availableYears.map((year, idx) => (
              <label key={year} className="flex items-center gap-1 text-sm cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={effectiveSelectedYears.includes(year)}
                  onChange={() => toggleYear(year)}
                  className="w-4 h-4"
                  style={{ accentColor: YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length] }}
                />
                <span className="font-medium" style={{ color: YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length] }}>
                  {year}
                </span>
              </label>
            ))}
          </div>

          {/* Bar chart */}
          <div style={{ height: 420 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData} margin={{ top: 8, right: 16, left: 16, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" tick={{ fontSize: 12 }} />
                <YAxis tickFormatter={currencyFormatter} tick={{ fontSize: 11 }} width={90} />
                <Tooltip formatter={currencyFormatter} />
                <Legend />
                <ReferenceLine y={0} stroke="#374151" strokeWidth={1.5} />
                {effectiveSelectedYears
                  .slice()
                  .sort()
                  .map(year => {
                    const idx = availableYears.indexOf(year);
                    return (
                      <Bar
                        key={year}
                        dataKey={String(year)}
                        name={String(year)}
                        fill={YEAR_BAR_COLORS[idx % YEAR_BAR_COLORS.length]}
                      />
                    );
                  })}
              </BarChart>
            </ResponsiveContainer>
          </div>

          <p className="text-xs text-gray-400 mt-3">{t('profit_formula_note')}</p>
        </div>
      )}
    </div>
  );
};

export default ProfitTab;
